import { useState } from 'react';
import { Box, Heading, Stack, Text, AccordionItemBody, Flex, Button, Icon } from '@chakra-ui/react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { NavLink } from 'react-router-dom';
import PrintButton from '@/components/ui/print-button';
import { LoginButton } from '@/components/ui/login-button';
import {
  AccordionItem,
  AccordionItemContent,
  AccordionItemTrigger,
  AccordionRoot,
} from '@/components/ui/accordion';
import { Prose } from '@/components/ui/prose';
import { toaster } from '@/components/ui/toaster';
import ContentDrawer from '@/pages/contents/content-drawer';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import { baseApi } from '@/utils/api';
import { Collection, Content } from '@/lib/types';

interface Item {
  collection: Collection;
  ContentWithStructure: {
    groups: {
      name: string;
      contents: string[];
      ContentValues: {
        reference_id: string;
        name: string;
        description: string;
        content_type_name: string;
      }[];
    }[];
  };
  User: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface Props {
  item: Item;
  isPublic: boolean;
}

export default function ViewContent({ item, isPublic = false }: Props) {
  const [selectedContent, setSelectedContent] = useState<Content>();
  const contentDrawer = useModalToggle();

  const handleView = async (referenceId: string) => {
    try {
      const response = await baseApi.get(`public/content/${referenceId}`);
      const content = await response.data.data;
      if (!content) {
        return;
      }
      setSelectedContent(content);
      contentDrawer.open();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch content',
      });
    }
  };

  const defaultValues = isPublic
    ? item.ContentWithStructure?.groups.map((_: unknown, index: number) => index + '')
    : ['0'];

  return (
    <Box pb={6} className='print-container'>
      <Flex justifyContent='space-between' alignItems='center' mb={6} mt={2}>
        <Heading as={'h1'} size={'2xl'}>
          {item.collection?.name}
        </Heading>
        {isPublic ? (
          <Flex gap={3}>
            <PrintButton documentTitle={item.collection?.name} variant='outline' />
            <LoginButton />
          </Flex>
        ) : (
          <Button colorPalette='brand' size='sm' mr={6} asChild>
            <NavLink to={`/public/collections/${item.collection?.reference_id}`} target='_blank'>
              Print
              <Icon as={PrinterIcon} w={4} h={4} />
            </NavLink>
          </Button>
        )}
      </Flex>
      <Stack gap={4}>
        <Box>
          <Prose size='md'>
            <div dangerouslySetInnerHTML={{ __html: item.collection?.description }} />
          </Prose>
        </Box>
        <AccordionRoot defaultValue={defaultValues} multiple>
          {item.ContentWithStructure?.groups.map((group, index: number) => (
            <AccordionItem key={index} value={index + ''}>
              <AccordionItemTrigger>
                <Box as='span' flex={1}>
                  <Text fontSize={'md'} fontWeight={'semibold'}>
                    {group.name}
                  </Text>
                </Box>
              </AccordionItemTrigger>
              <AccordionItemContent>
                <AccordionItemBody px={4}>
                  {group.ContentValues && group.ContentValues.length > 0 ? (
                    <Stack mt={3}>
                      {group.ContentValues.map((content) => (
                        <Box
                          key={content.reference_id}
                          py={1}
                          px={4}
                          bg={'gray.100'}
                          borderRadius={'md'}
                          transition='all 0.2s'
                          _hover={{
                            cursor: 'pointer',
                            bg: 'gray.200',
                            transform: 'translateY(-1px)',
                          }}
                          onClick={() => handleView(content.reference_id)}
                        >
                          <Text fontSize={'sm'} fontWeight={'medium'}>
                            {content.name}
                          </Text>
                          <Text fontSize={'xs'} fontStyle={'italic'}>
                            {content.content_type_name}
                          </Text>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Text>No content available</Text>
                  )}
                </AccordionItemBody>
              </AccordionItemContent>
            </AccordionItem>
          ))}
        </AccordionRoot>
      </Stack>

      {selectedContent && <ContentDrawer {...contentDrawer.props} content={selectedContent} />}
    </Box>
  );
}
